import { Badge, Container } from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { DateTime } from "luxon";
import React from "react";

import { Action, EventProps } from "../../types/StaticTypes";
import { createRenderingInfoPrinter } from "../../utils/debugging-utils";
import { CustomLuxonUtils } from "../Utilities/CustomLuxonUtils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    calendar: {
      marginTop: -10,
      marginLeft: window.innerWidth < 700 ? 0 : -15,
      padding: window.innerWidth < 700 ? "15px 0" : 15,
      [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(3),
        marginLeft: "auto",
      },
      "& .MuiPickersStaticWrapper-staticWrapperRoot": {
        backgroundColor: "transparent",
      },
      "& .MuiPickersBasePicker-pickerViewLandscape": {
        maxWidth: "100%",
        minWidth: "100%",
        "& .MuiPickersCalendarHeader-switchHeader": {
          color: "#000000",
          "& p": {
            fontWeight: "bold",
            fontSize: 16,
            textTransform: "capitalize",
          },
          "& .MuiPickersCalendarHeader-iconButton": {
            backgroundColor: "transparent",
            color: "#000000",
          },
        },
        "& .MuiPickersCalendarHeader-daysHeader": {
          justifyContent: "space-evenly",
          color: "#9B9B9B",
          paddingTop: 20,
          paddingBottom: 20,
          borderBottom: "1px solid #D8D8D8",
          "& .MuiPickersCalendarHeader-dayLabel": {
            fontSize: 14,
          },
        },
        "& .MuiPickersCalendar-transitionContainer": {
          minHeight: 340,
          padding: 15,
          [theme.breakpoints.up("md")]: {
            minHeight: 450,
          },
        },
        "& .MuiPickersCalendar-week": {
          justifyContent: "space-evenly",
          margin: `${theme.spacing(2)}px auto`,
          "& .MuiBadge-root": {
            "&.availabile-status .MuiBadge-dot": {
              backgroundColor: "#64a41d",
            },
            "&.not-available-status .MuiBadge-dot": {
              backgroundColor: "#d3d3d3",
            },
            "&.full-status .MuiBadge-dot": {
              backgroundColor: "#f5a623",
            },
            "&.blocked-status .MuiBadge-dot": {
              backgroundColor: "#dc3545",
            },
          },
          "& .MuiBadge-anchorOriginTopRightRectangle": {
            top: 6,
            right: 20,
            [theme.breakpoints.up("md")]: {
              top: 10,
              right: 25,
            },
          },
          "& .MuiPickersDay-day": {
            height: 38,
            width: 38,
            [theme.breakpoints.up("md")]: {
              height: 48,
              width: 48,
            },
            "& .MuiIconButton-label p": {
              fontSize: 12,
              fontWeight: 500,
              color: "#595959",
              [theme.breakpoints.up("md")]: {
                fontSize: 18,
              },
            },
          },
          "& .MuiPickersDay-day.MuiPickersDay-current": {
            backgroundColor: "#a9a6a6",
            "& .MuiIconButton-label p": {
              color: "#ffffff",
            },
          },
          "& .MuiPickersDay-day.MuiPickersDay-daySelected": {
            backgroundColor: "#000000",
            "& .MuiIconButton-label p": {
              color: "#ffffff",
            },
          },
        },
      },
    },
  })
);

type MonthCalendarProps = {
  events: EventProps[];
  children?: any;
  dispatch: React.Dispatch<Action>;
};

const printRenderingInfo = createRenderingInfoPrinter("ScheduleCalendar");

export const ScheduleCalendar: React.FC<MonthCalendarProps> = ({ events, dispatch }) => {
  const classes = useStyles();
  const [date, setDate] = React.useState<DateTime | null>(DateTime.now());

  const handleChange = (date: DateTime | null) => {
    dispatch({ type: "schedule", payload: { start: date } });
    return setDate(date);
  };

  printRenderingInfo();

  return (
    <Container className={classes.calendar}>
      <MuiPickersUtilsProvider utils={CustomLuxonUtils}>
        <DatePicker
          autoOk
          fullWidth
          disableToolbar
          variant="static"
          orientation="landscape"
          value={date}
          onChange={handleChange}
          onMonthChange={handleChange}
          showTodayButton={true}
          renderDay={(day, selectedDay, dayInCurrentMonth, dayComponent) => {
            const currentEvent =
              day &&
              events.find((event) => {
                return (day as any).c.day === new Date(event.start).getDate();
              });
            if (!currentEvent || !dayInCurrentMonth) return <>{dayComponent}</>;
            return currentEvent.allDay ? (
              <Badge variant="dot" className="blocked-status">
                {dayComponent}
              </Badge>
            ) : currentEvent.resource.full ? (
              <Badge variant="dot" className="full-status">
                {dayComponent}
              </Badge>
            ) : currentEvent.resource.available ? (
              <Badge variant="dot" className="availabile-status">
                {dayComponent}
              </Badge>
            ) : (
              <Badge variant="dot" className="not-available-status">
                {dayComponent}
              </Badge>
            );
          }}
        />
      </MuiPickersUtilsProvider>
    </Container>
  );
};
